import type { UtmParams } from '@ahd/shared/tools/get-utm-parameters';
import type { Facets } from '@ahd/shared/types/ahd-fleet-api/facets';
import type { Filter } from '@ahd/shared/types/ahd-fleet/filter';

import isUndefined from '@ahd/shared/tools/is-undefined';
import isUndefinedNullOrEmpty from '@ahd/shared/tools/is-undefined-null-or-empty';
import outputConsole from '@ahd/shared/tools/output-console';
import { type NavigateCar } from '@ahd/shared/types/ahd-fleet/navigate-car';
import { FrontendEnvironment } from '@ahd/shared/types/misc/environments';

import { type ConfigFilter, type ConfigObject, DebugLevel } from '../_configs/types';
import { standardFilter } from '../hooks/use-car-search/default';
import filterMap from '../modules/fahrzeugsuche/filter/maps/filterMap';
import { ENVIRONMENT_FRONTEND } from '../vars';
import stringToSlug from './string-to-slug';

import type { Modules } from '../modules/_definition/types';

type GenerateLinkProps = {
    module: Modules;
    settings: ConfigObject;
} & (
        | {
            car: Pick<NavigateCar, 'id' | 'name' | 'nameSlug'>;
            pathOnly: boolean;
            type: 'car';
        }
        | {
            allFacets: Facets;
            filter: Filter;
            pathOnly: boolean;
            type: 'filter' | 'landingpage';
            utmCampaign?: UtmParams;
        }
        | {
            type: 'host';
        }
        | {
            host?: string;
            idSource: string;
            pathOnly: boolean;
            type: 'source';
        }
    );

export default function generateLink(props: GenerateLinkProps): string {
    const { module, settings, type } = props;
    if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
        outputConsole('log', `${module} - generateLink`, `type`, type);
    }

    const host = `https://${(type === 'source' && props?.host) || ENVIRONMENT_FRONTEND === FrontendEnvironment.LOCALSERVER && !isUndefinedNullOrEmpty(settings?.api?.autoUncle?.origin) ? settings?.api?.autoUncle?.origin : location.host}`;
    const searchPath = settings?.style?.darkMode === true && !isUndefinedNullOrEmpty(settings?.general?.searchPathDark) ? settings?.general?.searchPathDark : settings?.general?.searchPath;
    const searchPathSearch = !isUndefinedNullOrEmpty(settings?.general?.searchPathSearch?.replace('?', '')) ? `?${settings?.general?.searchPathSearch?.replace('?', '')}` : '';
    if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
        outputConsole('log', `${module} - generateLink`, `host`, host);
        outputConsole('log', `${module} - generateLink`, `searchPath`, searchPath);
        outputConsole('log', `${module} - generateLink`, `searchPathSearch`, searchPathSearch);
    }

    const url = new URL(`${host}${searchPath}${searchPathSearch}`);

    switch (type) {
        case 'car': {
            const { car, pathOnly } = props;
            if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                outputConsole('log', `${module} - generateLink`, `car`, car);
                outputConsole('log', `${module} - generateLink`, `pathOnly`, pathOnly);
            }

            if (settings?.general?.disableSeoRoutes === true) {
                url.searchParams.set([car?.nameSlug || '', `carId${car?.id || ''}`]?.join('_'), null);
                return (pathOnly ? url?.href?.replace(url?.origin, '') : url?.href)?.replace('=null', '');
            } else {
                url.pathname = `${searchPath === '/' ? '' : searchPath}/c/${stringToSlug((car?.name || '-')?.replace(/\//g, '') || '-')}/${car?.id}`;
                return pathOnly ? url?.href?.replace(url?.origin, '') : url?.href;
            }
        }
        case 'filter':
        case 'landingpage': {
            const { allFacets, filter: selectedFilters, pathOnly, utmCampaign } = props;
            if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                outputConsole('log', `${module} - generateLink`, `allFacets`, allFacets);
                outputConsole('log', `${module} - generateLink`, `selectedFilters`, selectedFilters);
                outputConsole('log', `${module} - generateLink`, `pathOnly`, pathOnly);
            }

            const newFilter: Partial<Record<ConfigFilter, string>> = {};

            const selectedFiltersKeys = Object.keys(selectedFilters || {}) as (keyof Filter)[];
            const filterMapKeys = Object.keys(filterMap || {}) as (ConfigFilter | 'volltext')[];

            selectedFiltersKeys
                ?.filter((key) => {
                    return (
                        (key === 'moreCars' && selectedFilters?.[key] > standardFilter?.moreCars) ||
                        (key === 'pageSize' && selectedFilters?.[key] > standardFilter?.pageSize) ||
                        (![
                            'ignoreStandort',
                            'locExclude',
                            'locInclude',
                            'locSourceExclude',
                            'locSourceInclude',
                            'moreCars',
                            'nfz',
                            'nfzHersteller',
                            'order',
                            'pageSize',
                            'page.page',
                            'page.size',
                            'sellerExclude',
                            'sellerInclude',
                            'sort',
                        ]?.includes(key) &&
                            !isUndefinedNullOrEmpty(selectedFilters?.[key]) &&
                            selectedFilters?.[key] !== '-100' &&
                            selectedFilters?.[key] !== -100 &&
                            selectedFilters?.[key] !== false &&
                            isUndefined(settings?.fahrzeugsuche?.forcedFilter?.[key]))
                    );
                })
                ?.map((key) => {
                    const filterKey = filterMapKeys?.find((val) => {
                        return filterMap?.[val]?.filterKey === key;
                    });
                    const keyToUse = settings?.general?.disableSeoRoutes === true ? key : filterKey;
                    const filter = filterMap[filterKey];
                    const value = selectedFilters?.[key];

                    if (filter?.type === 'check') {
                        newFilter[keyToUse] = value === true ? 'Ja' : undefined;
                    } else if (filter?.type === 'multiselect') {
                        const facet = allFacets?.[filter?.facetKey];
                        newFilter[keyToUse] = facet
                            ?.filter((val) => (Array.isArray(value) ? value : [value])?.includes(val?.i))
                            ?.map((val) => stringToSlug(val?.t))
                            ?.join(',');
                    } else if (filter?.type === 'range') {
                        newFilter[keyToUse] = (value as number[])?.join(',');
                        if (newFilter[keyToUse] === ',') {
                            delete newFilter[keyToUse];
                        }
                    } else if (filter?.type === 'number' || (filter?.type === 'singleselect' && (filter?.filterKey?.endsWith('From') || filter?.filterKey?.endsWith('To')))) {
                        newFilter[keyToUse] = value;
                    } else if (filter?.type === 'singleselect') {
                        const facet = allFacets?.[filter?.facetKey];
                        newFilter[keyToUse] = stringToSlug(facet?.find((val) => value === val?.i)?.t);
                    } else if (filter?.type === 'text') {
                        newFilter[keyToUse] = keyToUse === 'volltext' ? encodeURIComponent(value as string) : value;
                    }
                });
            if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                outputConsole('log', `${module} - generateLink`, `newFilter`, newFilter);
            }

            Object.values(utmCampaign || {}).map((c) => typeof c === 'object' && (newFilter[c?.name] = c?.value));

            if (type === 'filter' && settings?.general?.disableSeoRoutes === true) {
                Object.entries(newFilter)?.map(([filterKey, filterValue]) => url.searchParams.set(filterKey, filterValue));
                return pathOnly ? url?.href?.replace(url?.origin, '') : url?.href;
            } else {
                let pathnameArray = [newFilter?.hersteller || '-', newFilter?.modell || '-', newFilter?.typ || '-', newFilter?.standort || '-'];
                while (pathnameArray[pathnameArray.length - 1] === '-') {
                    pathnameArray = pathnameArray.slice(0, pathnameArray.length - 1);
                }
                const joinedPathname = !isUndefinedNullOrEmpty(pathnameArray) ? `/${pathnameArray?.join('/')}` : '';
                url.pathname = `${type === 'filter' ? (searchPath === '/' ? '' : searchPath) : ''}${joinedPathname}`;
                Object.entries(newFilter)?.filter(([filterKey]) => !['hersteller', 'modell', 'typ', 'standort']?.includes(filterKey))?.map(([filterKey, filterValue]) => url.searchParams.set(filterKey, filterValue));
                return pathOnly ? url?.href?.replace(url?.origin, '') : url?.href;;
            }
        }
        case 'host': {
            return url?.origin;
        }
        case 'source': {
            const { idSource, pathOnly } = props;
            if (settings?.general?.debug === DebugLevel.ROUTER || settings?.general?.debug >= DebugLevel.INFO) {
                outputConsole('log', `${module} - generateLink`, `idSource`, idSource);
                outputConsole('log', `${module} - generateLink`, `pathOnly`, pathOnly);
            }

            if (settings?.general?.disableSeoRoutes === true) {
                url.searchParams.set('source', idSource);
                return pathOnly ? url?.href?.replace(url?.origin, '') : url?.href;
            } else {
                url.pathname = `${searchPath === '/' ? '' : searchPath}/source/${idSource}`;
                return pathOnly ? url?.href?.replace(url?.origin, '') : url?.href;
            }
        }
        default: {
            outputConsole('error', `${module} - generateLink`, `Type ${type} unknown`);
            return undefined;
        }
    }
}
